import React from "react";
import useSWR from "swr";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import { PageHeader, PageBody, StatTile } from "@/components/PageShell";
import { ArrowUpRight } from "@phosphor-icons/react";

const fetcher = (url) => api.get(url).then((r) => r.data);
const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Dashboard() {
  const { t, lang } = useI18n();
  const { data } = useSWR("/dashboard", fetcher);

  const pipeline = data?.pipeline || {};
  const pipelineList = [
    ["lead", pipeline.lead || 0],
    ["scheduled", pipeline.scheduled || 0],
    ["in_progress", pipeline.in_progress || 0],
    ["completed", pipeline.completed || 0],
    ["invoiced", pipeline.invoiced || 0],
  ];

  return (
    <div data-testid="dashboard-page">
      <PageHeader eyebrow="// 01 / Dashboard" title={t("dashboard.title")} sub={t("dashboard.sub")} testId="dashboard-header" />
      <PageBody>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 mb-10">
          <StatTile testId="stat-revenue-paid" label={t("dashboard.revenuePaid")} value={fmt$(data?.revenue_paid)} hint="ALL TIME" accent />
          <StatTile testId="stat-revenue-pending" label={t("dashboard.outstanding")} value={fmt$(data?.revenue_pending)} hint="OPEN" />
          <StatTile testId="stat-hours-week" label={t("dashboard.hoursWeek")} value={`${data?.hours_week ?? 0}h`} hint="MON → SUN" />
          <StatTile testId="stat-projects" label={t("dashboard.projectsKpi")} value={data?.project_count ?? 0} hint="ACTIVE" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <section className="lg:col-span-2 border border-zinc-200">
            <header className="border-b border-zinc-200 px-5 py-4 flex justify-between items-center">
              <h3 className="font-display font-bold text-lg">{t("dashboard.pipeline")}</h3>
              <Link to="/app/projects" className="label-eyebrow text-orange-600 hover:underline">{t("common.viewAll")} <ArrowUpRight size={14} className="inline" /></Link>
            </header>
            <div className="grid grid-cols-5">
              {pipelineList.map(([k, v], i) => (
                <div key={k} className={`p-5 ${i < 4 ? "border-r" : ""} border-zinc-200`}>
                  <div className="label-eyebrow text-zinc-500">{t(`status.${k}`)}</div>
                  <div className="font-display font-black text-3xl mt-2 tracking-tighter">{v}</div>
                </div>
              ))}
            </div>
            <div className="border-t border-zinc-200">
              <div className="px-5 py-3 label-eyebrow text-zinc-500">{t("dashboard.recentProjects")}</div>
              {(data?.recent_projects || []).length === 0 ? (
                <div className="px-5 py-8 text-sm text-zinc-500">{t("projects.empty")}.</div>
              ) : (
                <ul>
                  {data.recent_projects.map((p) => (
                    <li key={p.id} className="px-5 py-3 border-t border-zinc-100 flex justify-between items-center">
                      <Link to={`/app/projects/${p.id}`} className="text-sm font-medium hover:text-orange-600">{p.name}</Link>
                      <span className="label-eyebrow text-zinc-500">{t(`status.${p.status}`) || p.status}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          <div className="space-y-6">
            <section className="border border-zinc-200">
              <header className="border-b border-zinc-200 px-5 py-4 flex justify-between items-center">
                <h3 className="font-display font-bold text-lg">{t("dashboard.followUps")}</h3>
                <Link to="/app/projects" className="label-eyebrow text-orange-600 hover:underline">{t("nav.projects")}</Link>
              </header>
              {(data?.follow_ups || []).length === 0 ? (
                <div className="px-5 py-8 text-sm text-zinc-500">{lang === "fr" ? "Aucune relance prévue." : "No follow-ups scheduled."}</div>
              ) : (
                <ul>
                  {data.follow_ups.map(p => (
                    <li key={p.id} className="px-5 py-3 border-t border-zinc-100 first:border-t-0">
                      <div className="mono text-xs text-orange-600 font-bold">{p.next_follow_up}</div>
                      <Link to={`/app/projects/${p.id}`} className="text-sm font-medium hover:text-orange-600">{p.customer_name}</Link>
                      <div className="text-xs text-zinc-500">{p.name}</div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="border border-zinc-200">
              <header className="border-b border-zinc-200 px-5 py-4 flex justify-between items-center">
                <h3 className="font-display font-bold text-lg">{t("dashboard.upcoming")}</h3>
                <Link to="/app/calendar" className="label-eyebrow text-orange-600 hover:underline">{t("nav.calendar")}</Link>
              </header>
              {(data?.upcoming_events || []).length === 0 ? (
                <div className="px-5 py-8 text-sm text-zinc-500">{lang === "fr" ? "Aucun événement." : "Nothing scheduled."}</div>
              ) : (
                <ul>
                  {data.upcoming_events.map((e) => (
                    <li key={e.id} className="px-5 py-3 border-t border-zinc-100 first:border-t-0">
                      <div className="mono text-xs text-zinc-500">{e.date} · {e.start_time}–{e.end_time}</div>
                      <div className="font-semibold text-sm mt-1">{e.title}</div>
                      <div className="text-xs text-zinc-500">{e.crew}</div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </div>
      </PageBody>
    </div>
  );
}
